import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { defer, finalize, Observable, tap } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { DfService } from '../../../../core/df/df.service';
import { InvoiceOps } from '../../../../core/df/df.types';
import { aiRecommendationLabel } from '../../../../core/status/status-meta.types';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { IconComponent } from '../../../../shared/icon/icon';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/confirm-dialog/confirm-dialog';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';
import { readError } from '../../../../core/http/read-error';

@Component({
  selector: 'app-df-invoice-ops-detail',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    RouterLink,
    DatePipe,
    StatusChipComponent,
    IconComponent,
    ZarPipe,
    DashPipe,
  ],
  templateUrl: './df-invoice-ops-detail.html',
  styleUrl: './df-invoice-ops-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfInvoiceOpsDetailComponent implements OnInit {
  private readonly dfService = inject(DfService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly invoiceId = this.route.snapshot.paramMap.get('invoiceId') ?? '';

  protected readonly invoice = signal<InvoiceOps | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly isActioning = signal(false);
  protected readonly serverError = signal<string | null>(null);

  protected readonly fundForm = this.fb.group({
    funded_amount: ['', [Validators.required, Validators.min(1)]],
    cc_ref: [''],
    note: [''],
  });

  /**
   * §C6.2: up to 80% of the invoice is Ops authority; above it a Credit Committee
   * reference is required (the server enforces this and the debtor's authorised
   * tier). A Credit Committee login does not replace the reference.
   */
  protected needsCcWarning(): boolean {
    const amt = parseFloat(this.fundForm.controls.funded_amount.value ?? '0');
    const invoiceAmount = parseFloat(this.invoice()?.invoice_amount ?? '0');
    return invoiceAmount > 0 && amt / invoiceAmount > 0.8;
  }

  protected readonly aiRecLabel = aiRecommendationLabel;

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.dfService
      .opsGetInvoice(this.invoiceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inv) => {
          this.invoice.set(inv);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected verify(): void {
    this.confirmAndRun(
      {
        title: 'Verify this invoice?',
        message: 'Mark this invoice as verified. The debtor will be eligible for funding.',
        confirmLabel: 'Verify',
      },
      () => this.dfService.opsVerifyInvoice(this.invoiceId),
      'Invoice verified.',
    );
  }

  protected fund(): void {
    if (this.fundForm.invalid) {
      this.fundForm.markAllAsTouched();
      return;
    }
    const v = this.fundForm.getRawValue();
    this.confirmAndRun(
      {
        title: 'Fund this invoice?',
        message: `Fund ZAR ${v.funded_amount} against this invoice. This cannot be undone.`,
        confirmLabel: 'Fund',
      },
      () =>
        this.dfService.opsFundInvoice(this.invoiceId, {
          funded_amount: v.funded_amount ?? '',
          cc_ref: v.cc_ref ?? undefined,
          note: v.note ?? undefined,
        }),
      'Invoice funded.',
      this.fundForm,
    );
  }

  protected settle(): void {
    this.confirmAndRun(
      {
        title: 'Settle this invoice?',
        message: 'Mark this invoice as settled.',
        confirmLabel: 'Settle',
      },
      () => this.dfService.opsSettleInvoice(this.invoiceId),
      'Invoice settled.',
    );
  }

  protected reject(): void {
    this.confirmAndRun(
      {
        title: 'Reject this invoice?',
        message: 'Reject this invoice. The applicant will be notified.',
        confirmLabel: 'Reject',
        destructive: true,
      },
      () => this.dfService.opsRejectInvoice(this.invoiceId),
      'Invoice rejected.',
    );
  }

  /**
   * The dialog owns the write, so a failure keeps the dialog and its input;
   * only a completed write closes it (Tier 2 §4b).
   */
  private confirmAndRun(
    data: ConfirmDialogData,
    write: () => Observable<InvoiceOps>,
    message: string,
    form?: FormGroup,
  ): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      data: {
        ...data,
        confirm: () =>
          defer(() => {
            this.isActioning.set(true);
            this.serverError.set(null);
            this.cdr.markForCheck();
            return write().pipe(
              tap((inv) => {
                this.invoice.set(inv);
                this.snackBar.open(message, 'Dismiss', { duration: 5000 });
              }),
              finalize(() => {
                this.isActioning.set(false);
                this.cdr.markForCheck();
              }),
            );
          }),
        errorMessage: (err: unknown) => readError(err),
        resetOnClose: () => form?.reset(),
      } satisfies ConfirmDialogData,
    });
  }
}
